import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { CheckCircle, Shield, TrendingDown, Star, ExternalLink, Filter, Search, Grid, Table, X, Check, Calculator, MessageCircle, Send, Loader2, ChevronDown, ChevronUp, BookOpen, Clock, ArrowLeft, Calendar, Tag, Settings, BarChart3, Zap, FileText, TrendingUp, RefreshCw, Play, User, LogOut, LogIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ========================================
// AUTHENTICATION CONTEXT & COMPONENTS
// ========================================
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Create Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth Callback Component - Handles OAuth redirect
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { checkAuth } = useAuth();

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/admin', { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Exchange session_id for session_token
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId
        }, { withCredentials: true });

        // Refresh auth state
        await checkAuth();

        // Check if user is admin
        if (response.data.is_admin) {
          navigate('/admin', { replace: true, state: { user: response.data } });
        } else {
          // Not admin - redirect to home with message
          navigate('/', { replace: true, state: { authError: 'Admin access required. Only valueseeker101@gmail.com can access the admin area.' } });
        }
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/', { replace: true, state: { authError: 'Authentication failed. Please try again.' } });
      }
    };

    processAuth();
  }, [location, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Admin Route Component
const ProtectedAdminRoute = ({ children }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If user passed from AuthCallback, skip check
    if (location.state?.user) {
      setChecking(false);
      return;
    }

    if (!loading) {
      setChecking(false);
    }
  }, [loading, location.state]);

  if (checking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Admin Access Required</CardTitle>
            <CardDescription>
              Sign in with Google to access the admin dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              Only authorized administrators can access this area.
            </p>
            <Button 
              onClick={login}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="admin-login-btn"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Sign in with Google
            </Button>
            <div className="text-center">
              <Link to="/" className="text-sm text-emerald-600 hover:underline">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-900">Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this area
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              Signed in as: <strong>{user.email}</strong>
            </p>
            <p className="text-sm text-slate-500 text-center">
              Only valueseeker101@gmail.com can access the admin dashboard.
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/">
                <Button variant="outline" className="w-full">
                  ← Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
};

// User Menu Component - Shows login/logout in header
const UserMenu = () => {
  const { user, login, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  if (!user) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={login}
        className="flex items-center gap-2"
        data-testid="header-login-btn"
      >
        <LogIn className="h-4 w-4" />
        Sign In
      </Button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="user-menu-btn"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
            <User className="h-4 w-4 text-white" />
          </div>
        )}
        <span className="text-sm font-medium text-slate-700 hidden md:block">{user.name}</span>
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
          <div className="px-4 py-2 border-b">
            <p className="text-sm font-medium text-slate-900">{user.name}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          {user.is_admin && (
            <Link 
              to="/admin" 
              className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
              onClick={() => setShowMenu(false)}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Admin Dashboard
            </Link>
          )}
          <button
            onClick={() => {
              logout();
              setShowMenu(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 inline mr-2" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [savingsStats, setSavingsStats] = useState(null);
  const [featuredProviders, setFeaturedProviders] = useState([]);

  useEffect(() => {
    fetchSavingsStats();
    fetchFeaturedProviders();
  }, []);

  const fetchSavingsStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/savings`);
      setSavingsStats(response.data);
    } catch (error) {
      console.error('Error fetching savings stats:', error);
    }
  };

  const fetchFeaturedProviders = async () => {
    try {
      const response = await axios.get(`${API}/providers?min_rating=4.0`);
      setFeaturedProviders(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-20 w-auto mr-4" />
              <span className="text-5xl font-bold text-slate-900">Value Seeker</span>
            </div>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/savings-calculator" className="text-slate-700 hover:text-emerald-600 transition-colors">Calculator</Link>
                <Link to="/guides" className="text-slate-700 hover:text-emerald-600 transition-colors">Guides</Link>
                <Link to="/about" className="text-slate-700 hover:text-emerald-600 transition-colors">About</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Stop Wasting Money on{' '}
                <span className="text-emerald-600">Rental Car Insurance</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Rental companies charge up to £25/day for insurance that doesn't even cover everything. 
                Our guide helps you skip their overpriced cover and get full protection for as little as £40 per year.
              </p>
              
              {savingsStats && (
                <div className="bg-emerald-50 rounded-xl p-6 mb-8 border border-emerald-200">
                  <div className="flex items-center mb-3">
                    <TrendingDown className="h-6 w-6 text-emerald-600 mr-2" />
                    <span className="text-emerald-800 font-semibold">Realistic Trip Savings</span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-900">
                        £{savingsStats.one_week_savings}
                      </div>
                      <p className="text-emerald-700 text-sm">
                        One week trip savings<br/>
                        <span className="text-xs">(£{savingsStats.one_week_rental_cost} vs £{savingsStats.average_annual_cost})</span>
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-900">
                        £{savingsStats.two_week_savings}
                      </div>
                      <p className="text-emerald-700 text-sm">
                        Two week trip savings<br/>
                        <span className="text-xs">(£{savingsStats.two_week_rental_cost} vs £{savingsStats.average_annual_cost})</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-center border-t border-emerald-200 pt-3">
                    <p className="text-emerald-700 text-sm">
                      <strong>Annual policy pays for itself after just {savingsStats.annual_breakeven_days} days</strong> of rental
                    </p>
                    <p className="text-emerald-600 text-xs mt-1">
                      Policies from £{savingsStats.cheapest_annual_policy} - £{savingsStats.most_expensive_annual_policy} per year
                    </p>
                  </div>
                </div>
              )}

              <Link to="/compare">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
                  data-testid="compare-providers-btn"
                >
                  Compare Excess Insurance Providers →
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1529369623266-f5264b696110?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Professional with rental car"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Independent Insurance?</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Don't fall for expensive rental desk insurance. Here's why independent policies are better value.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-emerald-100 hover:border-emerald-200 transition-colors">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingDown className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-emerald-900">Save Money</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-center mb-4">
                  Annual policies cost a fraction of rental desk rates
                </p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-center mb-3">
                    <div className="font-bold text-slate-800">One Week Holiday Example:</div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">Rental desk insurance:</span>
                    <span className="font-bold text-red-600">£175</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">Annual CDW policy:</span>
                    <span className="font-bold text-emerald-600">£30-45</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                    <span className="text-slate-700 font-medium">You save:</span>
                    <span className="font-bold text-emerald-700">£130-145</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-200 transition-colors">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-emerald-900">Better Cover</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-center mb-4">
                  Includes tyres, windscreen, roof, underbody, and more
                </p>
                <div className="space-y-2">
                  {['Tyres & Wheels', 'Windscreen', 'Roof & Mirrors', 'Underbody', 'Interior'].map((item) => (
                    <div key={item} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                      <span className="text-slate-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-200 transition-colors">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-emerald-900">No Surprises</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-center mb-4">
                  Get clear, transparent policies with no hidden catches
                </p>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center text-emerald-700">
                      <CheckCircle className="h-3 w-3 mr-2" />
                      Clear terms & conditions
                    </li>
                    <li className="flex items-center text-emerald-700">
                      <CheckCircle className="h-3 w-3 mr-2" />
                      No hidden fees
                    </li>
                    <li className="flex items-center text-emerald-700">
                      <CheckCircle className="h-3 w-3 mr-2" />
                      Transparent excess amounts
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Who We Are</h2>
              <p className="text-xl text-slate-300 mb-6">
                Value Seeker is an independent comparison platform on a mission to stop UK travellers 
                being overcharged for car rental insurance.
              </p>
              <p className="text-slate-400 mb-6">
                We've all been there - standing at the rental desk, tired after a long flight, being pressured 
                into buying expensive insurance that costs more than the car rental itself. We started Value Seeker 
                because we believe there's a better way.
              </p>
              <p className="text-slate-400 mb-8">
                Our platform compares all major UK CDW insurance providers so you can find comprehensive 
                coverage at a fraction of the rental desk price. We're completely free to use, and our 
                recommendations aren't influenced by who pays us the most commission.
              </p>
              
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-400">50+</div>
                  <div className="text-sm text-slate-400">Expert Guides</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-400">5</div>
                  <div className="text-sm text-slate-400">Trusted Providers</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-400">£130+</div>
                  <div className="text-sm text-slate-400">Avg. Savings</div>
                </div>
              </div>

              <Link to="/compare">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Start Comparing Now
                </Button>
              </Link>
            </div>
            
            <div className="space-y-6">
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">100% Independent</h3>
                      <p className="text-slate-300 text-sm">
                        We're not owned by any insurance company. Our comparisons are unbiased and focused on finding you the best deal.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">FCA Regulated Providers</h3>
                      <p className="text-slate-300 text-sm">
                        Every provider we feature is regulated by the Financial Conduct Authority, giving you peace of mind.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingDown className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Real Savings, No Tricks</h3>
                      <p className="text-slate-300 text-sm">
                        Our customers save an average of £130+ per trip. The prices you see are the prices you pay - no hidden fees.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Providers Preview */}
      {featuredProviders.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Top Rated Providers</h2>
              <p className="text-xl text-slate-600">
                Compare the best UK CDW insurance providers
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {featuredProviders.map((provider) => (
                <Card key={provider.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      {provider.name}
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                        <Star className="h-3 w-3 mr-1" />
                        {provider.customer_rating}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{provider.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Annual from:</span>
                        <span className="font-bold text-emerald-600">£{provider.annual_price_from}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Excess:</span>
                        <span className="font-semibold">£{provider.excess_amount}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {provider.includes_tyres && <Badge variant="outline" className="text-xs">Tyres</Badge>}
                        {provider.includes_windscreen && <Badge variant="outline" className="text-xs">Windscreen</Badge>}
                        {provider.includes_roof && <Badge variant="outline" className="text-xs">Roof</Badge>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/compare">
                <Button variant="outline" size="lg" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                  View All Providers & Compare
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Start Saving on Car Rental Insurance Today
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Compare all UK CDW providers in one place and find the perfect policy for your needs.
          </p>
          <Link to="/compare">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-slate-50 px-8 py-4 text-lg rounded-xl shadow-lg"
              data-testid="cta-compare-btn"
            >
              Compare Providers Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Legal Compliance Notice */}
      <div className="bg-blue-50 border-t border-blue-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-blue-700 mb-2">
              <strong>Important:</strong> We do not provide financial advice. Information shown is for comparison only.
            </p>
            <p className="text-xs text-blue-600">
              Value Seeker may earn commission from featured providers. This does not affect prices you pay or our rankings. 
              All providers are FCA regulated. <Link to="/legal" className="underline hover:no-underline">Read full legal information</Link>.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img src="/logo.png" alt="Value Seeker" className="h-10 w-auto mr-2" />
                <span className="text-2xl font-bold">Value Seeker</span>
              </div>
              <p className="text-slate-400 text-sm">
                Independent comparison platform helping UK consumers save money on car rental insurance.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <div className="space-y-2">
                <Link to="/legal" className="block text-slate-400 hover:text-white text-sm transition-colors">Terms & Privacy</Link>
                <Link to="/legal" className="block text-slate-400 hover:text-white text-sm transition-colors">Cookie Policy</Link>
                <Link to="/legal" className="block text-slate-400 hover:text-white text-sm transition-colors">FCA Compliance</Link>
                <Link to="/about" className="block text-slate-400 hover:text-white text-sm transition-colors">About Us</Link>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Contact</h3>
              <div className="space-y-2 text-sm text-slate-400">
                <a 
                  href="mailto:support@valueseeker.co.uk" 
                  className="block hover:text-white transition-colors"
                  data-testid="footer-email-link"
                >
                  support@valueseeker.co.uk
                </a>
                <Link 
                  to="/legal#contact" 
                  className="block hover:text-white transition-colors"
                  data-testid="footer-complaints-link"
                >
                  Questions & Complaints
                </Link>
                <p className="text-xs mt-4">
                  Response within 5 business days
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p className="text-sm">© 2024 Value Seeker Ltd. All rights reserved.</p>
            <p className="mt-2 text-xs">
              We earn commission from featured providers. This does not affect your costs or our rankings.
              All featured insurance providers are regulated by the Financial Conduct Authority.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Comparison Table Component
const ComparisonTable = ({ providers, affiliateUrls = {}, onTrackClick }) => {
  if (providers.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <p className="text-slate-600 text-lg">No providers to compare. Add some filters or clear existing ones.</p>
        </CardContent>
      </Card>
    );
  }

  const handleGetQuoteClick = (providerId) => {
    if (onTrackClick) {
      onTrackClick(providerId);
    }
  };

  const features = [
    { key: 'includes_tyres', label: 'Tyres & Wheels' },
    { key: 'includes_windscreen', label: 'Windscreen' },
    { key: 'includes_roof', label: 'Roof & Mirrors' },
    { key: 'includes_underbody', label: 'Underbody' },
    { key: 'includes_interior', label: 'Interior' }
  ];

  return (
    <div className="overflow-x-auto bg-white rounded-lg border shadow-sm">
      <div className="min-w-max">
        <table className="w-full border-collapse">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 font-semibold text-slate-900 bg-white sticky left-0 z-10 border-r w-48">Features</th>
              {providers.map((provider) => (
                <th key={provider.id} className="text-center p-3 font-semibold text-slate-900 min-w-44 border-r last:border-r-0">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="font-bold text-xs leading-tight text-center">{provider.name}</div>
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-xs">
                      <Star className="h-3 w-3 mr-1" />
                      {provider.customer_rating}
                    </Badge>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Annual Price Row */}
            <tr className="border-t hover:bg-slate-25">
              <td className="p-4 font-medium text-slate-700 bg-slate-25 sticky left-0 z-10 border-r">Annual Price</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-price`} className="p-3 text-center border-r last:border-r-0">
                  <div className="text-base font-bold text-emerald-600">
                    £{provider.annual_price_from}
                    {provider.annual_price_to !== provider.annual_price_from && (
                      <span className="text-xs text-slate-600 block"> - £{provider.annual_price_to}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">per year</div>
                </td>
              ))}
            </tr>

            {/* Excess Amount Row */}
            <tr className="border-t bg-slate-25 hover:bg-slate-50">
              <td className="p-4 font-medium text-slate-700 bg-slate-25 sticky left-0 z-10 border-r">Excess Amount</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-excess`} className="p-3 text-center border-r last:border-r-0">
                  <div className="font-semibold">£{provider.excess_amount}</div>
                  {provider.excess_amount === 0 && (
                    <div className="text-xs text-emerald-600 font-medium">No Excess</div>
                  )}
                </td>
              ))}
            </tr>

            {/* Coverage Type Row */}
            <tr className="border-t hover:bg-slate-25">
              <td className="p-4 font-medium text-slate-700 bg-white sticky left-0 z-10 border-r">Coverage Type</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-coverage`} className="p-3 text-center border-r last:border-r-0">
                  <Badge 
                    variant={provider.coverage_type === 'comprehensive' ? 'default' : 'secondary'}
                    className={`text-xs ${provider.coverage_type === 'comprehensive' ? 'bg-emerald-600' : ''}`}
                  >
                    {provider.coverage_type.charAt(0).toUpperCase() + provider.coverage_type.slice(1)}
                  </Badge>
                </td>
              ))}
            </tr>

            {/* Geographic Coverage Row */}
            <tr className="border-t bg-slate-25 hover:bg-slate-50">
              <td className="p-4 font-medium text-slate-700 bg-slate-25 sticky left-0 z-10 border-r">Geographic Coverage</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-geographic`} className="p-3 text-center text-sm border-r last:border-r-0">
                  <span className="capitalize">{provider.geographic_coverage.replace('_', ' ')}</span>
                </td>
              ))}
            </tr>

            {/* Max Vehicle Value Row */}
            <tr className="border-t hover:bg-slate-25">
              <td className="p-4 font-medium text-slate-700 bg-white sticky left-0 z-10 border-r">Max Vehicle Value</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-vehicle`} className="p-3 text-center text-sm border-r last:border-r-0">
                  £{provider.max_vehicle_value.toLocaleString()}
                </td>
              ))}
            </tr>

            {/* Feature Rows */}
            {features.map((feature, index) => (
              <tr key={feature.key} className={`border-t hover:bg-slate-25 ${index % 2 === 0 ? 'bg-slate-25' : ''}`}>
                <td className={`p-4 font-medium text-slate-700 sticky left-0 z-10 border-r ${index % 2 === 0 ? 'bg-slate-25' : 'bg-white'}`}>
                  {feature.label}
                </td>
                {providers.map((provider) => (
                  <td key={`${provider.id}-${feature.key}`} className="p-3 text-center border-r last:border-r-0">
                    {provider[feature.key] ? (
                      <div className="flex justify-center">
                        <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                          <X className="h-3 w-3 text-slate-400" />
                        </div>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}

            {/* Reviews Row */}
            <tr className="border-t bg-slate-25 hover:bg-slate-50">
              <td className="p-4 font-medium text-slate-700 bg-slate-25 sticky left-0 z-10 border-r">Customer Reviews</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-reviews`} className="p-3 text-center border-r last:border-r-0">
                  <div className="text-xs font-medium">{provider.total_reviews.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">reviews</div>
                </td>
              ))}
            </tr>

            {/* Action Row */}
            <tr className="border-t bg-emerald-50">
              <td className="p-4 font-medium text-slate-700 bg-emerald-50 sticky left-0 z-10 border-r">Get Quote</td>
              {providers.map((provider) => (
                <td key={`${provider.id}-action`} className="p-3 text-center border-r last:border-r-0">
                  <a 
                    href={affiliateUrls[provider.id]?.affiliate_url || provider.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer sponsored"
                    className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1 rounded-md transition-colors duration-200"
                    data-testid={`table-get-quote-${provider.id}`}
                    onClick={() => handleGetQuoteClick(provider.id)}
                  >
                    Get Quote
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Comparison Page Component
const ComparisonPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [affiliateUrls, setAffiliateUrls] = useState({});
  const [filters, setFilters] = useState({
    maxAnnualPrice: '',
    minRating: '',
    coverageType: '',
    geographicCoverage: ''
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filters.maxAnnualPrice) queryParams.append('max_annual_price', filters.maxAnnualPrice);
      if (filters.minRating) queryParams.append('min_rating', filters.minRating);
      if (filters.coverageType) queryParams.append('coverage_type', filters.coverageType);
      if (filters.geographicCoverage) queryParams.append('geographic_coverage', filters.geographicCoverage);

      const response = await axios.get(`${API}/providers?${queryParams.toString()}`);
      setProviders(response.data);
      
      // Fetch affiliate URLs for all providers
      const urlPromises = response.data.map(async (provider) => {
        try {
          const urlResponse = await axios.get(`${API}/affiliate-url/${provider.id}`);
          return { [provider.id]: urlResponse.data };
        } catch (error) {
          console.error(`Error fetching affiliate URL for ${provider.name}:`, error);
          return { [provider.id]: { affiliate_url: provider.website_url, network: 'Direct' } };
        }
      });
      
      const urlResults = await Promise.all(urlPromises);
      const urlMap = urlResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setAffiliateUrls(urlMap);
      
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Track click events for analytics
  const trackClick = async (providerId) => {
    try {
      await axios.post(`${API}/track/click`, {
        provider_id: providerId,
        click_type: 'get_quote'
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const applyFilters = () => {
    fetchProviders();
  };

  const clearFilters = () => {
    setFilters({
      maxAnnualPrice: '',
      minRating: '',
      coverageType: '',
      geographicCoverage: ''
    });
    setTimeout(fetchProviders, 100);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-emerald-600 font-semibold">Compare</Link>
                <Link to="/about" className="text-slate-700 hover:text-emerald-600 transition-colors">About</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Compare CDW Insurance Providers</h1>
          <p className="text-xl text-slate-600 mb-4">
            Find the perfect car rental excess insurance policy for your needs
          </p>
          
          <LegalNotice />
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filter & Search
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-600 mr-2">View:</span>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="flex items-center"
                  data-testid="table-view-btn"
                >
                  <Table className="h-4 w-4 mr-1" />
                  Table
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="flex items-center"
                  data-testid="cards-view-btn"
                >
                  <Grid className="h-4 w-4 mr-1" />
                  Cards
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Max Annual Price (£)
                </label>
                <input
                  type="number"
                  value={filters.maxAnnualPrice}
                  onChange={(e) => handleFilterChange('maxAnnualPrice', e.target.value)}
                  placeholder="e.g. 100"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Min Rating
                </label>
                <select
                  value={filters.minRating}
                  onChange={(e) => handleFilterChange('minRating', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Any Rating</option>
                  <option value="4.0">4+ Stars</option>
                  <option value="3.5">3.5+ Stars</option>
                  <option value="3.0">3+ Stars</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Coverage Type
                </label>
                <select
                  value={filters.coverageType}
                  onChange={(e) => handleFilterChange('coverageType', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">All Types</option>
                  <option value="comprehensive">Comprehensive</option>
                  <option value="basic">Basic</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Geographic Coverage
                </label>
                <select
                  value={filters.geographicCoverage}
                  onChange={(e) => handleFilterChange('geographicCoverage', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">All Regions</option>
                  <option value="uk_only">UK Only</option>
                  <option value="europe">Europe</option>
                  <option value="worldwide">Worldwide</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={applyFilters} className="bg-emerald-600 hover:bg-emerald-700">
                Apply Filters
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            <p className="mt-4 text-slate-600">Loading providers...</p>
          </div>
        ) : (
          <>
            {providers.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <p className="text-slate-600 text-lg">No providers found matching your criteria.</p>
                  <Button variant="outline" onClick={clearFilters} className="mt-4">
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {viewMode === 'table' ? (
                  <div data-testid="providers-table">
                    <ComparisonTable providers={providers} affiliateUrls={affiliateUrls} onTrackClick={trackClick} />
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="providers-list">
                    {providers.map((provider) => (
                      <Card key={provider.id} className="hover:shadow-lg transition-shadow" data-testid={`provider-card-${provider.id}`}>
                        <CardContent className="p-6">
                          <div className="grid lg:grid-cols-4 gap-6">
                            {/* Provider Info */}
                            <div className="lg:col-span-1">
                              <div className="flex items-start justify-between mb-3">
                                <h3 className="text-xl font-bold text-slate-900">{provider.name}</h3>
                                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                                  <Star className="h-3 w-3 mr-1" />
                                  {provider.customer_rating}
                                </Badge>
                              </div>
                              <p className="text-slate-600 text-sm mb-4">{provider.description}</p>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-slate-500">Reviews:</span>
                                  <span className="ml-2 font-medium">{provider.total_reviews.toLocaleString()}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Coverage:</span>
                                  <span className="ml-2 font-medium capitalize">{provider.geographic_coverage.replace('_', ' ')}</span>
                                </div>
                              </div>
                            </div>

                            {/* Pricing */}
                            <div className="lg:col-span-1">
                              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                                <div className="text-2xl font-bold text-emerald-900">
                                  £{provider.annual_price_from}
                                  {provider.annual_price_to !== provider.annual_price_from && (
                                    <span className="text-base text-slate-600"> - £{provider.annual_price_to}</span>
                                  )}
                                </div>
                                <div className="text-sm text-emerald-700">per year</div>
                                <Separator className="my-3" />
                                <div className="text-sm">
                                  <div className="text-slate-600">Excess: <span className="font-semibold">£{provider.excess_amount}</span></div>
                                  {provider.daily_price_from && (
                                    <div className="text-slate-600 mt-1">Daily: £{provider.daily_price_from}+</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Coverage Features */}
                            <div className="lg:col-span-1">
                              <h4 className="font-semibold text-slate-900 mb-3">What's Covered</h4>
                              <div className="space-y-2">
                                {[
                                  { key: 'includes_tyres', label: 'Tyres & Wheels' },
                                  { key: 'includes_windscreen', label: 'Windscreen' },
                                  { key: 'includes_roof', label: 'Roof & Mirrors' },
                                  { key: 'includes_underbody', label: 'Underbody' },
                                  { key: 'includes_interior', label: 'Interior' }
                                ].map(({ key, label }) => (
                                  <div key={key} className="flex items-center text-sm">
                                    {provider[key] ? (
                                      <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                                    ) : (
                                      <div className="h-4 w-4 rounded-full border-2 border-slate-300 mr-2" />
                                    )}
                                    <span className={provider[key] ? 'text-slate-700' : 'text-slate-400'}>
                                      {label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="lg:col-span-1 flex flex-col justify-center space-y-3">
                              <a 
                                href={affiliateUrls[provider.id]?.affiliate_url || provider.website_url} 
                                target="_blank" 
                                rel="noopener noreferrer sponsored"
                                className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md transition-colors duration-200 font-medium"
                                data-testid={`get-quote-${provider.id}`}
                                onClick={() => trackClick(provider.id)}
                              >
                                Get Quote
                                <ExternalLink className="h-4 w-4 ml-2" />
                              </a>
                              <div className="text-xs text-slate-500 text-center">
                                Max vehicle value: £{provider.max_vehicle_value.toLocaleString()}
                              </div>
                              {affiliateUrls[provider.id]?.network && affiliateUrls[provider.id].network !== 'Direct' && (
                                <div className="text-xs text-emerald-600 text-center font-medium">
                                  via {affiliateUrls[provider.id].network}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Legal Compliance Component
const LegalNotice = () => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
    <div className="flex">
      <div className="ml-3">
        <p className="text-sm text-blue-700">
          <strong>Important:</strong> Value Seeker is a comparison service. We do not provide financial advice. 
          You should consider whether the products featured on this website are appropriate for your circumstances and seek independent advice if needed.
          We may earn commission from providers featured on this site.
        </p>
      </div>
    </div>
  </div>
);

// FCA Compliance Footer Component
const ComplianceFooter = () => (
  <div className="bg-slate-100 py-8 mt-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-8 text-sm text-slate-600">
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Regulatory Information</h3>
          <p className="mb-2">
            Value Seeker operates as a comparison service for car hire excess insurance products. 
            We are not regulated by the Financial Conduct Authority (FCA) as we do not provide advice or sell insurance directly.
          </p>
          <p className="mb-2">
            All insurance products featured are provided by FCA-regulated insurers. 
            You should check the regulatory status of any provider before purchasing.
          </p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">How We Make Money</h3>
          <p className="mb-2">
            This service is free for consumers. We earn commission from insurance providers when customers purchase policies through our links. 
            This does not affect the prices you pay or our comparison methodology.
          </p>
          <p className="mb-2">
            We aim to show a representative range of products, but not all providers or products are featured.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// About Page Component  
const AboutPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/about" className="text-emerald-600 font-semibold">About</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">About Value Seeker</h1>
        
        <LegalNotice />
        
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-slate-600 mb-8">
            Value Seeker is an independent comparison platform dedicated to helping UK consumers make informed decisions about car rental insurance.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Our Mission</h2>
          <p className="text-slate-700 mb-6">
            We believe consumers shouldn't be misled into purchasing overpriced, limited insurance at rental desks. 
            Our mission is to educate travelers about better alternatives and help them find comprehensive coverage at a fraction of the cost.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Why We Exist</h2>
          <p className="text-slate-700 mb-6">
            Rental companies often charge £25+ per day for basic collision damage waiver insurance that excludes many common damages like tyres, windscreen, and underbody damage. Meanwhile, comprehensive annual policies from specialist insurers cost as little as £32.99 per year and provide better coverage.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">How We Work</h2>
          <p className="text-slate-700 mb-6">
            We aggregate information from all major UK CDW insurance providers, allowing you to compare policies on key factors like price, coverage, excess amounts, and customer ratings. Our platform is completely free to use.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Revenue & Transparency</h2>
          <p className="text-slate-700 mb-6">
            We earn commission when customers purchase policies through our affiliate links with insurance providers. 
            This commission does not affect the prices you pay - you'll pay the same amount whether you come through Value Seeker or go directly to the provider.
            Our commission arrangements do not influence our comparison methodology or the order in which providers are displayed.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">Important Information</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <ul className="text-slate-700 space-y-2">
              <li>• We do not provide financial advice - information shown is for comparison purposes only</li>
              <li>• You should read policy terms and conditions before purchasing</li>
              <li>• All featured insurers are regulated by the Financial Conduct Authority (FCA)</li>
              <li>• We aim to show a representative range but do not feature all available products</li>
              <li>• Prices and product details are updated regularly but may change</li>
            </ul>
          </div>
        </div>
      </div>

      <ComplianceFooter />
    </div>
  );
};

// Legal Page Component
const LegalPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/about" className="text-slate-700 hover:text-emerald-600 transition-colors">About</Link>
                <Link to="/legal" className="text-emerald-600 font-semibold">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Legal Information</h1>
        
        <div className="space-y-8">
          {/* FCA Compliance */}
          <Card>
            <CardHeader>
              <CardTitle>Regulatory Compliance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <p>
                  <strong>Value Seeker</strong> operates as a comparison service for car hire excess insurance products. 
                  We do not provide regulated financial advice or sell insurance products directly.
                </p>
                <p>
                  All insurance providers featured on our platform are regulated by the <strong>Financial Conduct Authority (FCA)</strong>. 
                  You can verify their regulatory status on the FCA register at <a href="https://register.fca.org.uk" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">register.fca.org.uk</a>.
                </p>
                <p>
                  As we provide comparison services only and do not offer advice, we are not required to be FCA regulated. 
                  However, we adhere to high standards of transparency and consumer protection.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Affiliate Disclosure */}
          <Card>
            <CardHeader>
              <CardTitle>Affiliate Relationships & Commission</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <p>
                  <strong>How We Make Money:</strong> Value Seeker earns commission from insurance providers when customers purchase policies through our affiliate links. This is how we keep our service free for consumers.
                </p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-900 mb-2">Important: No Extra Cost to You</h4>
                  <p className="text-emerald-800">
                    Our commission arrangements do not affect the prices you pay. You will pay the same amount whether you 
                    purchase through Value Seeker or go directly to the provider.
                  </p>
                </div>
                <p>
                  Commission rates vary by provider but typically range from 5-15% of the premium paid. 
                  These arrangements do not influence our comparison methodology, product rankings, or editorial content.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Terms of Service */}
          <Card>
            <CardHeader>
              <CardTitle>Terms of Service</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <h4 className="font-semibold">Use of This Website</h4>
                <ul className="list-disc list-inside space-y-2">
                  <li>Information is provided for comparison purposes only</li>
                  <li>We do not guarantee the accuracy of all information</li>
                  <li>Prices and product details may change without notice</li>
                  <li>You should verify details directly with providers before purchasing</li>
                </ul>

                <h4 className="font-semibold mt-6">Your Responsibilities</h4>
                <ul className="list-disc list-inside space-y-2">
                  <li>Read policy terms and conditions carefully before purchasing</li>
                  <li>Ensure products are suitable for your circumstances</li>
                  <li>Seek independent advice if you are unsure about any product</li>
                  <li>Check regulatory status of providers before purchasing</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Policy */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy & Data Protection</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <p>
                  We are committed to protecting your privacy and comply with UK data protection laws including GDPR.
                </p>
                
                <h4 className="font-semibold">Information We Collect</h4>
                <ul className="list-disc list-inside space-y-2">
                  <li>Website usage data (pages visited, time spent)</li>
                  <li>Device information (browser type, IP address)</li>
                  <li>Cookies for website functionality and analytics</li>
                </ul>

                <h4 className="font-semibold mt-4">How We Use Information</h4>
                <ul className="list-disc list-inside space-y-2">
                  <li>To improve our website and services</li>
                  <li>To track affiliate referrals for commission purposes</li>
                  <li>For analytics to understand user behavior</li>
                </ul>

                <p className="text-sm bg-slate-100 p-3 rounded">
                  We do not sell personal information to third parties. When you click on provider links, 
                  you will be subject to their privacy policies.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Policy */}
          <Card>
            <CardHeader>
              <CardTitle>Cookie Policy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <p>
                  We use cookies to improve your experience and track affiliate referrals. 
                  By using our website, you consent to our use of cookies.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <h5 className="font-medium">Essential Cookies</h5>
                    <p className="text-sm">Required for basic website functionality</p>
                  </div>
                  <div>
                    <h5 className="font-medium">Analytics Cookies</h5>
                    <p className="text-sm">Help us understand how visitors use our site</p>
                  </div>
                  <div>
                    <h5 className="font-medium">Affiliate Cookies</h5>
                    <p className="text-sm">Track referrals for commission purposes</p>
                  </div>
                  <div>
                    <h5 className="font-medium">Preference Cookies</h5>
                    <p className="text-sm">Remember your choices and settings</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card id="contact">
            <CardHeader>
              <CardTitle>Contact & Complaints</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-slate-700">
                <p>
                  If you have any questions about our service or need to make a complaint:
                </p>
                <div className="bg-slate-100 p-4 rounded-lg space-y-2">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a 
                      href="mailto:support@valueseeker.co.uk" 
                      className="text-emerald-600 hover:text-emerald-700 hover:underline"
                      data-testid="legal-email-link"
                    >
                      support@valueseeker.co.uk
                    </a>
                  </p>
                  <p><strong>Address:</strong> Value Seeker Ltd, UK</p>
                  <p><strong>Response Time:</strong> We aim to respond within 5 business days</p>
                </div>
                <p className="text-sm">
                  For complaints about insurance products, you should contact the provider directly. 
                  If unresolved, you may be able to refer the matter to the Financial Ombudsman Service.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Savings Calculator Page Component
const SavingsCalculatorPage = () => {
  const [formData, setFormData] = useState({
    tripDays: 7,
    deskPricePerDay: 25,
    annualPolicyPrice: 45,
    tripsPerYear: 2
  });
  const [results, setResults] = useState(null);

  const calculateSavings = () => {
    const { tripDays, deskPricePerDay, annualPolicyPrice, tripsPerYear } = formData;
    
    // Single trip comparison
    const rentalDeskCost = tripDays * deskPricePerDay;
    const singleTripSavings = rentalDeskCost - annualPolicyPrice;
    
    // Annual comparison (multiple trips)
    const annualRentalDeskCost = rentalDeskCost * tripsPerYear;
    const annualSavings = annualRentalDeskCost - annualPolicyPrice;
    
    // Break-even calculation
    const breakEvenDays = Math.ceil(annualPolicyPrice / deskPricePerDay);
    
    // Percentage savings
    const savingsPercentage = Math.round((singleTripSavings / rentalDeskCost) * 100);
    const annualSavingsPercentage = Math.round((annualSavings / annualRentalDeskCost) * 100);

    setResults({
      rentalDeskCost,
      singleTripSavings,
      annualRentalDeskCost,
      annualSavings,
      breakEvenDays,
      savingsPercentage,
      annualSavingsPercentage
    });
  };

  useEffect(() => {
    calculateSavings();
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.max(0, parseInt(value) || 0)
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/savings-calculator" className="text-emerald-600 font-semibold">Calculator</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Calculator className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Car Hire Insurance Savings Calculator</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            See exactly how much you could save by choosing an annual excess policy over rental desk insurance
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Input */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calculator className="h-5 w-5 mr-2 text-emerald-600" />
                Your Trip Details
              </CardTitle>
              <CardDescription>
                Adjust the values below to calculate your potential savings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Trip Duration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Trip Duration (days)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={formData.tripDays}
                    onChange={(e) => handleInputChange('tripDays', e.target.value)}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    data-testid="trip-days-slider"
                  />
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      value={formData.tripDays}
                      onChange={(e) => handleInputChange('tripDays', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-center font-semibold"
                      min="1"
                      max="365"
                      data-testid="trip-days-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Average UK car rental is 5-10 days</p>
              </div>

              {/* Rental Desk Price */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rental Desk Insurance Price (£/day)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={formData.deskPricePerDay}
                    onChange={(e) => handleInputChange('deskPricePerDay', e.target.value)}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                    data-testid="desk-price-slider"
                  />
                  <div className="w-20 text-center">
                    <div className="flex items-center border border-slate-300 rounded">
                      <span className="px-2 bg-slate-100 text-slate-600">£</span>
                      <input
                        type="number"
                        value={formData.deskPricePerDay}
                        onChange={(e) => handleInputChange('deskPricePerDay', e.target.value)}
                        className="w-full px-2 py-1 text-center font-semibold border-none focus:outline-none"
                        min="5"
                        max="100"
                        data-testid="desk-price-input"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Typical rental desk charge: £20-30/day</p>
              </div>

              {/* Annual Policy Price */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Annual CDW Policy Price (£/year)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={formData.annualPolicyPrice}
                    onChange={(e) => handleInputChange('annualPolicyPrice', e.target.value)}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    data-testid="annual-price-slider"
                  />
                  <div className="w-20 text-center">
                    <div className="flex items-center border border-slate-300 rounded">
                      <span className="px-2 bg-slate-100 text-slate-600">£</span>
                      <input
                        type="number"
                        value={formData.annualPolicyPrice}
                        onChange={(e) => handleInputChange('annualPolicyPrice', e.target.value)}
                        className="w-full px-2 py-1 text-center font-semibold border-none focus:outline-none"
                        min="20"
                        max="200"
                        data-testid="annual-price-input"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Annual policies typically £30-50</p>
              </div>

              {/* Trips Per Year */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Car Rentals Per Year
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="12"
                    value={formData.tripsPerYear}
                    onChange={(e) => handleInputChange('tripsPerYear', e.target.value)}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    data-testid="trips-slider"
                  />
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      value={formData.tripsPerYear}
                      onChange={(e) => handleInputChange('tripsPerYear', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-center font-semibold"
                      min="1"
                      max="52"
                      data-testid="trips-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Average UK traveler: 1-2 rentals/year</p>
              </div>
            </CardContent>
          </Card>

          {/* Results Display */}
          <div className="space-y-6">
            {results && (
              <>
                {/* Single Trip Savings */}
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-emerald-900">Your {formData.tripDays}-Day Trip Savings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-4 bg-white rounded-lg">
                        <div className="text-red-600 text-sm font-medium mb-1">Rental Desk Would Cost</div>
                        <div className="text-2xl font-bold text-red-700">£{results.rentalDeskCost}</div>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg">
                        <div className="text-emerald-600 text-sm font-medium mb-1">Annual Policy Cost</div>
                        <div className="text-2xl font-bold text-emerald-700">£{formData.annualPolicyPrice}</div>
                      </div>
                    </div>
                    
                    <div className="text-center p-6 bg-emerald-600 rounded-xl text-white">
                      <div className="text-emerald-100 text-sm mb-1">You Save</div>
                      <div className="text-4xl font-bold mb-2">
                        £{results.singleTripSavings}
                      </div>
                      <div className="text-emerald-200">
                        That's {results.savingsPercentage}% savings on this trip alone!
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Annual Savings */}
                {formData.tripsPerYear > 1 && (
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-blue-900">Annual Savings ({formData.tripsPerYear} trips)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center p-4 bg-white rounded-lg">
                          <div className="text-red-600 text-sm font-medium mb-1">Total Desk Insurance</div>
                          <div className="text-2xl font-bold text-red-700">£{results.annualRentalDeskCost}</div>
                        </div>
                        <div className="text-center p-4 bg-white rounded-lg">
                          <div className="text-blue-600 text-sm font-medium mb-1">One Annual Policy</div>
                          <div className="text-2xl font-bold text-blue-700">£{formData.annualPolicyPrice}</div>
                        </div>
                      </div>
                      
                      <div className="text-center p-6 bg-blue-600 rounded-xl text-white">
                        <div className="text-blue-100 text-sm mb-1">Total Annual Savings</div>
                        <div className="text-4xl font-bold mb-2">
                          £{results.annualSavings}
                        </div>
                        <div className="text-blue-200">
                          {results.annualSavingsPercentage}% saved across all your trips
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Break-even Info */}
                <Card className="shadow-lg">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-center space-x-4">
                      <div className="text-center">
                        <div className="text-slate-600 text-sm">Break-even point:</div>
                        <div className="text-2xl font-bold text-slate-900">{results.breakEvenDays} days</div>
                        <div className="text-xs text-slate-500">
                          After {results.breakEvenDays} rental days, every additional day is pure savings
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* CTA */}
                <div className="text-center">
                  <Link to="/compare">
                    <Button 
                      size="lg" 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg rounded-xl shadow-lg"
                      data-testid="compare-providers-cta"
                    >
                      Compare Annual CDW Policies →
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">How the Calculator Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-600 font-bold">1</span>
                </div>
                <h3 className="font-semibold mb-2">Rental Desk Calculation</h3>
                <p className="text-slate-600 text-sm">
                  We multiply the daily rental desk insurance rate by your trip length 
                  to show what you'd pay at the counter.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-emerald-600 font-bold">2</span>
                </div>
                <h3 className="font-semibold mb-2">Annual Policy Comparison</h3>
                <p className="text-slate-600 text-sm">
                  Compare against a single annual policy price which covers 
                  unlimited rentals throughout the year.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-blue-600 font-bold">3</span>
                </div>
                <h3 className="font-semibold mb-2">Your Savings</h3>
                <p className="text-slate-600 text-sm">
                  See your exact savings per trip and annually, 
                  plus the break-even point in rental days.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Legal Notice */}
        <LegalNotice />
      </div>
    </div>
  );
};

// AI Chat Widget Component
const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm your CDW insurance assistant. How can I help you today? You can ask me about:\n\n• What is CDW insurance?\n• How much can I save?\n• Which provider is best for me?\n• Coverage differences"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai/assistant/chat?query=${encodeURIComponent(userMessage)}`
      );
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I couldn't process your request. Please try again or visit our comparison page to explore options."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
        data-testid="chat-widget-toggle"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-slate-200" data-testid="chat-widget-window">
          {/* Header */}
          <div className="bg-emerald-600 text-white px-4 py-3 flex items-center">
            <img src="/logo.png" alt="Value Seeker" className="h-8 w-auto mr-2 rounded" />
            <div>
              <div className="font-semibold">CDW Insurance Assistant</div>
              <div className="text-xs text-emerald-200">Powered by AI</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-none'
                      : 'bg-white text-slate-800 shadow-sm border border-slate-200 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{message.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-slate-800 shadow-sm border border-slate-200 rounded-2xl rounded-bl-none px-4 py-2">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about CDW insurance..."
                className="flex-1 px-4 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-full flex items-center justify-center transition-colors"
                data-testid="chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Guides List Page Component
const GuidesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, published, draft
  const [publishing, setPublishing] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, title

  useEffect(() => {
    fetchArticles();
  }, [filter]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/articles${statusParam}`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const publishArticle = async (articleId) => {
    try {
      setPublishingId(articleId);
      await axios.post(`${API}/ai/publisher/publish/${articleId}`);
      await fetchArticles();
    } catch (error) {
      console.error('Error publishing article:', error);
      alert('Failed to publish article. Please try again.');
    } finally {
      setPublishingId(null);
    }
  };

  const publishAllDrafts = async () => {
    const draftArticles = articles.filter(a => a.status === 'draft');
    if (draftArticles.length === 0) {
      alert('No draft articles to publish!');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to publish ${draftArticles.length} draft articles?`)) {
      return;
    }
    
    setPublishing(true);
    let published = 0;
    let failed = 0;
    
    for (const article of draftArticles) {
      try {
        await axios.post(`${API}/ai/publisher/publish/${article.id}`);
        published++;
      } catch (error) {
        console.error(`Failed to publish: ${article.title}`, error);
        failed++;
      }
    }
    
    setPublishing(false);
    alert(`Published ${published} articles. ${failed > 0 ? `${failed} failed.` : ''}`);
    await fetchArticles();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      guides: 'bg-blue-100 text-blue-800',
      comparisons: 'bg-purple-100 text-purple-800',
      tips: 'bg-amber-100 text-amber-800',
      news: 'bg-red-100 text-red-800',
      reviews: 'bg-green-100 text-green-800'
    };
    return colors[category] || 'bg-slate-100 text-slate-800';
  };

  // Filter and search articles
  const filteredArticles = articles
    .filter(article => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        article.title?.toLowerCase().includes(query) ||
        article.summary?.toLowerCase().includes(query) ||
        article.keywords?.some(k => k.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (sortBy === 'title') {
        return (a.title || '').localeCompare(b.title || '');
      }
      return 0;
    });

  const draftCount = articles.filter(a => a.status === 'draft').length;
  const publishedCount = articles.filter(a => a.status === 'published').length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/savings-calculator" className="text-slate-700 hover:text-emerald-600 transition-colors">Calculator</Link>
                <Link to="/guides" className="text-emerald-600 font-semibold">Guides</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <BookOpen className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Car Hire Insurance Guides</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Expert guides and tips to help you save money on rental car insurance
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="filter-all"
            >
              All Articles ({articles.length})
            </button>
            <button
              onClick={() => setFilter('published')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'published' 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="filter-published"
            >
              Published ({publishedCount})
            </button>
            <button
              onClick={() => setFilter('draft')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'draft' 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="filter-draft"
            >
              Drafts ({draftCount})
            </button>
          </div>
        </div>

        {/* Search and Sort Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search articles by title, content, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="article-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            data-testid="article-sort"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="title">A-Z by Title</option>
          </select>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div className="mb-4 text-sm text-slate-600">
            Found <strong>{filteredArticles.length}</strong> article{filteredArticles.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </div>
        )}

        {/* Admin Controls Panel */}
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <span className="text-sm text-amber-800">
                    <strong>{draftCount}</strong> drafts pending review
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                  <span className="text-sm text-emerald-800">
                    <strong>{publishedCount}</strong> published
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className="text-sm text-amber-700">
                  Review articles below, then publish individually or all at once
                </span>
                <Button
                  onClick={publishAllDrafts}
                  disabled={publishing || draftCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400"
                  data-testid="publish-all-btn"
                >
                  {publishing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Publish All Drafts ({draftCount})
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto" />
            <p className="mt-4 text-slate-600">Loading articles...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {searchQuery ? 'No articles found' : 'No articles yet'}
              </h3>
              <p className="text-slate-500">
                {searchQuery 
                  ? `No articles match "${searchQuery}". Try a different search term.`
                  : filter === 'published' 
                    ? 'No published articles yet. Check back soon!' 
                    : 'Articles are being generated. Please check back soon!'}
              </p>
              {searchQuery && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {filteredArticles.map((article) => (
              <Card 
                key={article.id} 
                className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                data-testid={`article-card-${article.id}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={getCategoryColor(article.category)}>
                      {article.category || 'guide'}
                    </Badge>
                    <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                      {article.status}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg leading-tight hover:text-emerald-600 transition-colors">
                    <Link to={`/guides/${article.slug}`}>
                      {article.title}
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {article.summary || article.content?.substring(0, 150) + '...'}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {article.reading_time_minutes || 5} min read
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {article.keywords && article.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {article.keywords.slice(0, 3).map((keyword, idx) => (
                        <span 
                          key={idx} 
                          className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <Link 
                      to={`/guides/${article.slug}`}
                      className="inline-flex items-center text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                    >
                      Review Article
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Link>
                    
                    {article.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          publishArticle(article.id);
                        }}
                        disabled={publishingId === article.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
                        data-testid={`publish-btn-${article.id}`}
                      >
                        {publishingId === article.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Publish
                          </>
                        )}
                      </Button>
                    )}
                    {article.status === 'published' && (
                      <span className="text-xs text-emerald-600 font-medium flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Live
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats Section */}
        {!loading && filteredArticles.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-slate-500">
              Showing {filteredArticles.length} of {articles.length} article{articles.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <Card className="bg-emerald-50 border-emerald-200 inline-block">
            <CardContent className="py-6 px-8">
              <h3 className="font-semibold text-emerald-900 mb-2">Ready to save on your next rental?</h3>
              <p className="text-emerald-700 text-sm mb-4">
                Compare CDW insurance providers and find the best deal for you.
              </p>
              <Link to="/compare">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Compare Providers
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Single Article Page Component
const ArticlePage = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Get slug from URL
  const slug = window.location.pathname.split('/guides/')[1];

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles/slug/${slug}`);
      setArticle(response.data);
      setEditedContent(response.data.content || '');
      setEditedTitle(response.data.title || '');
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/articles/${article.id}`, {
        title: editedTitle,
        content: editedContent
      });
      // Refresh article
      await fetchArticle();
      setIsEditing(false);
      alert('Article saved successfully!');
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(article.content || '');
    setEditedTitle(article.title || '');
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, index) => {
      // H2 headers
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl font-bold text-slate-900 mt-8 mb-4">{line.slice(3)}</h2>;
      }
      // H3 headers
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-xl font-semibold text-slate-800 mt-6 mb-3">{line.slice(4)}</h3>;
      }
      // Bullet points
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <li key={index} className="ml-6 mb-2 text-slate-700 list-disc">
            {line.slice(2)}
          </li>
        );
      }
      // Bold text
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={index} className="mb-4 text-slate-700 leading-relaxed">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
            )}
          </p>
        );
      }
      // Empty lines
      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }
      // Regular paragraphs
      return <p key={index} className="mb-4 text-slate-700 leading-relaxed">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Link to="/" className="flex items-center">
                <img src="/logo.png" alt="Value Seeker" className="h-10 w-auto mr-2" />
                <span className="text-2xl font-bold text-slate-900">Value Seeker</span>
              </Link>
            </div>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or hasn't been published yet.</p>
          <Link to="/guides">
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Guides
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Value Seeker" className="h-16 w-auto mr-3" />
              <span className="text-4xl font-bold text-slate-900">Value Seeker</span>
            </Link>
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-slate-700 hover:text-emerald-600 transition-colors">Home</Link>
                <Link to="/compare" className="text-slate-700 hover:text-emerald-600 transition-colors">Compare</Link>
                <Link to="/savings-calculator" className="text-slate-700 hover:text-emerald-600 transition-colors">Calculator</Link>
                <Link to="/guides" className="text-emerald-600 font-semibold">Guides</Link>
                <Link to="/legal" className="text-slate-700 hover:text-emerald-600 transition-colors">Legal</Link>
              </nav>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link 
          to="/guides" 
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Guides
        </Link>

        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Badge className="bg-blue-100 text-blue-800">
              {article.category || 'guide'}
            </Badge>
            {article.status === 'draft' && (
              <Badge variant="secondary">Draft</Badge>
            )}
          </div>
          
          {/* Editable Title */}
          {isEditing ? (
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="text-4xl font-bold text-slate-900 mb-4 leading-tight w-full border-2 border-emerald-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="edit-title-input"
            />
          ) : (
            <h1 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
              {article.title}
            </h1>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 text-slate-500 text-sm">
              <span className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {formatDate(article.published_at || article.created_at)}
              </span>
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {article.reading_time_minutes || 5} min read
              </span>
              <span className="flex items-center">
                <BookOpen className="h-4 w-4 mr-2" />
                {article.word_count || 0} words
              </span>
            </div>
            
            {/* Edit Controls */}
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    size="sm"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={saving}
                    data-testid="save-article-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="outline"
                  size="sm"
                  data-testid="edit-article-btn"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Edit Article
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Keywords */}
        {article.keywords && article.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b">
            <Tag className="h-4 w-4 text-slate-400" />
            {article.keywords.map((keyword, idx) => (
              <span 
                key={idx} 
                className="text-sm bg-slate-100 text-slate-600 px-3 py-1 rounded-full"
              >
                {keyword}
              </span>
            ))}
          </div>
        )}

        {/* Article Body - Editable */}
        {isEditing ? (
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Article Content (Markdown supported)
            </label>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-96 p-4 border-2 border-emerald-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Write your article content here..."
              data-testid="edit-content-textarea"
            />
            <p className="text-xs text-slate-500 mt-2">
              Use ## for headings, - for bullet points, **text** for bold
            </p>
          </div>
        ) : (
          <div className="prose prose-lg max-w-none" data-testid="article-content">
            {renderContent(article.content)}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 p-8 bg-emerald-50 rounded-2xl border border-emerald-200">
          <h3 className="text-xl font-bold text-emerald-900 mb-3">
            Ready to save on car hire insurance?
          </h3>
          <p className="text-emerald-700 mb-4">
            Compare CDW insurance providers and find the best deal for your next trip.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/compare">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Compare Providers
              </Button>
            </Link>
            <Link to="/savings-calculator">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                Calculate Savings
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Articles suggestion */}
        <div className="mt-12 text-center">
          <Link 
            to="/guides" 
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            ← Browse more guides
          </Link>
        </div>
      </article>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [seoReport, setSeoReport] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchDashboard();
    fetchSeoReport();
    fetchClickStats();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/dashboard`);
      setDashboard(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoReport = async () => {
    try {
      const response = await axios.get(`${API}/admin/seo-report`);
      setSeoReport(response.data);
    } catch (error) {
      console.error('Error fetching SEO report:', error);
    }
  };

  const fetchClickStats = async () => {
    try {
      const response = await axios.get(`${API}/track/stats`);
      setClickStats(response.data);
    } catch (error) {
      console.error('Error fetching click stats:', error);
    }
  };

  const handleAutoOptimize = async () => {
    if (!window.confirm('This will optimize all draft articles and automatically publish those with good SEO scores. Continue?')) {
      return;
    }
    
    setActionLoading('optimize');
    try {
      const response = await axios.post(`${API}/admin/auto-optimize-and-publish`);
      alert(`Started optimization for ${response.data.articles_queued} articles. This runs in the background.`);
      // Refresh after a few seconds
      setTimeout(() => {
        fetchDashboard();
        fetchSeoReport();
      }, 5000);
    } catch (error) {
      alert('Failed to start optimization. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkPublish = async () => {
    if (!window.confirm('This will publish ALL draft articles immediately. Are you sure?')) {
      return;
    }
    
    setActionLoading('publish');
    try {
      const response = await axios.post(`${API}/admin/bulk-publish-all`);
      alert(`Published ${response.data.published_count} articles!`);
      fetchDashboard();
      fetchSeoReport();
    } catch (error) {
      alert('Failed to publish articles. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateMore = async () => {
    const count = window.prompt('How many articles do you want to generate?', '10');
    if (!count) return;
    
    setActionLoading('generate');
    try {
      const response = await axios.post(`${API}/ai/batch/generate-articles?count=${count}&category=guides`);
      alert(`Started generating ${count} articles in the background!`);
      setTimeout(fetchDashboard, 10000);
    } catch (error) {
      alert('Failed to start article generation. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto" />
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const seo = dashboard?.seo_overview || {};

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-emerald-400 mr-3" />
              <div>
                <h1 className="text-xl font-bold">Admin Dashboard</h1>
                <p className="text-slate-400 text-sm">Value Seeker Content Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-slate-400 text-sm">
                Last updated: {formatDate(lastRefresh)}
              </span>
              <Button
                onClick={() => { fetchDashboard(); fetchSeoReport(); fetchClickStats(); }}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Link to="/">
                <Button variant="outline" size="sm" className="border-emerald-500 text-emerald-400 hover:bg-emerald-900">
                  View Site
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">{summary.total_articles || 0}</div>
                <div className="text-sm text-slate-500">Total Articles</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">{summary.draft_count || 0}</div>
                <div className="text-sm text-amber-700">Drafts</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">{summary.published_count || 0}</div>
                <div className="text-sm text-emerald-700">Published</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{summary.avg_seo_score || 0}</div>
                <div className="text-sm text-blue-700">Avg SEO Score</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{summary.avg_word_count || 0}</div>
                <div className="text-sm text-purple-700">Avg Words</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-600">{summary.content_health_score || 0}%</div>
                <div className="text-sm text-slate-500">Health Score</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Automation Controls */}
        <Card className="mb-8 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
          <CardHeader>
            <CardTitle className="flex items-center text-white">
              <Zap className="h-6 w-6 mr-2" />
              Automation Controls
            </CardTitle>
            <CardDescription className="text-emerald-100">
              One-click actions to manage your content automatically
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <Button
                onClick={handleAutoOptimize}
                disabled={actionLoading === 'optimize'}
                className="bg-white text-emerald-700 hover:bg-emerald-50 h-auto py-4"
                data-testid="auto-optimize-btn"
              >
                {actionLoading === 'optimize' ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <TrendingUp className="h-5 w-5 mr-2" />
                )}
                <div className="text-left">
                  <div className="font-semibold">Auto-Optimize & Publish</div>
                  <div className="text-xs opacity-75">Optimize SEO and publish quality articles</div>
                </div>
              </Button>
              
              <Button
                onClick={handleBulkPublish}
                disabled={actionLoading === 'publish' || summary.draft_count === 0}
                className="bg-white text-emerald-700 hover:bg-emerald-50 h-auto py-4"
                data-testid="bulk-publish-btn"
              >
                {actionLoading === 'publish' ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Play className="h-5 w-5 mr-2" />
                )}
                <div className="text-left">
                  <div className="font-semibold">Publish All ({summary.draft_count || 0})</div>
                  <div className="text-xs opacity-75">Publish all drafts immediately</div>
                </div>
              </Button>
              
              <Button
                onClick={handleGenerateMore}
                disabled={actionLoading === 'generate'}
                className="bg-white text-emerald-700 hover:bg-emerald-50 h-auto py-4"
                data-testid="generate-more-btn"
              >
                {actionLoading === 'generate' ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <FileText className="h-5 w-5 mr-2" />
                )}
                <div className="text-left">
                  <div className="font-semibold">Generate More Articles</div>
                  <div className="text-xs opacity-75">Create new AI-powered content</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* SEO Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                SEO Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {seoReport && (
                <div className="space-y-6">
                  {/* SEO Score Distribution */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">Score Distribution</h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <div className="w-24 text-sm text-slate-600">High (70+)</div>
                        <div className="flex-1 bg-slate-200 rounded-full h-4 overflow-hidden">
                          <div 
                            className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${(seoReport.performance_breakdown?.high_seo / seoReport.total_articles) * 100 || 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-right text-sm font-medium">{seoReport.performance_breakdown?.high_seo || 0}</div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 text-sm text-slate-600">Medium</div>
                        <div className="flex-1 bg-slate-200 rounded-full h-4 overflow-hidden">
                          <div 
                            className="bg-amber-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${(seoReport.performance_breakdown?.medium_seo / seoReport.total_articles) * 100 || 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-right text-sm font-medium">{seoReport.performance_breakdown?.medium_seo || 0}</div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 text-sm text-slate-600">Needs Work</div>
                        <div className="flex-1 bg-slate-200 rounded-full h-4 overflow-hidden">
                          <div 
                            className="bg-red-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${(seoReport.performance_breakdown?.needs_improvement / seoReport.total_articles) * 100 || 0}%` }}
                          />
                        </div>
                        <div className="w-12 text-right text-sm font-medium">{seoReport.performance_breakdown?.needs_improvement || 0}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Keywords */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">Top Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {seoReport.top_keywords?.slice(0, 10).map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {kw.keyword} ({kw.count})
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{seoReport.unique_keywords || 0}</div>
                      <div className="text-xs text-slate-500">Unique Keywords</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{seoReport.avg_word_count || 0}</div>
                      <div className="text-xs text-slate-500">Avg Word Count</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-slate-600" />
                  Recent Articles
                </span>
                <Link to="/guides">
                  <Button variant="outline" size="sm">View All</Button>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.recent_articles?.map((article) => (
                  <div key={article.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <Link to={`/guides/${article.id}`} className="font-medium text-slate-900 hover:text-emerald-600 truncate block">
                        {article.title}
                      </Link>
                      <div className="flex items-center space-x-3 text-xs text-slate-500 mt-1">
                        <span>{article.word_count} words</span>
                        {article.seo_score && <span>SEO: {article.seo_score}</span>}
                      </div>
                    </div>
                    <Badge 
                      variant={article.status === 'published' ? 'default' : 'secondary'}
                      className={article.status === 'published' ? 'bg-emerald-600' : ''}
                    >
                      {article.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Click Analytics Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
              Click Analytics - Get Quote Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clickStats && (
              <div>
                {/* Totals */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-purple-600">{clickStats.totals?.all_time || 0}</div>
                    <div className="text-sm text-purple-700">Total Clicks</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600">{clickStats.totals?.last_7_days || 0}</div>
                    <div className="text-sm text-blue-700">Last 7 Days</div>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <div className="text-3xl font-bold text-emerald-600">{clickStats.totals?.today || 0}</div>
                    <div className="text-sm text-emerald-700">Today</div>
                  </div>
                </div>
                
                {/* Provider breakdown */}
                <h4 className="font-medium text-slate-700 mb-3">Clicks by Provider</h4>
                <div className="space-y-2">
                  {clickStats.providers?.map((provider) => (
                    <div key={provider.provider_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <span className="font-medium text-slate-900">{provider.provider_name}</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-slate-500">Today: <strong>{provider.today_clicks}</strong></span>
                        <span className="text-slate-500">Week: <strong>{provider.week_clicks}</strong></span>
                        <span className="text-purple-600 font-semibold">Total: {provider.total_clicks}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {clickStats.totals?.all_time === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <p>No clicks tracked yet. Clicks will appear here when users click "Get Quote" buttons.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="mt-8">
          <CardContent className="py-6">
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/guides">
                <Button variant="outline">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Manage Articles
                </Button>
              </Link>
              <Link to="/compare">
                <Button variant="outline">
                  <Shield className="h-4 w-4 mr-2" />
                  View Providers
                </Button>
              </Link>
              <Link to="/savings-calculator">
                <Button variant="outline">
                  <Calculator className="h-4 w-4 mr-2" />
                  Savings Calculator
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Live Site
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// App Router - Handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/compare" element={<ComparisonPage />} />
      <Route path="/savings-calculator" element={<SavingsCalculatorPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/guides/:slug" element={<ArticlePage />} />
      <Route path="/admin" element={
        <ProtectedAdminRoute>
          <AdminDashboard />
        </ProtectedAdminRoute>
      } />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/legal" element={<LegalPage />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <AIChatWidget />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;